"use client";

import { useState } from "react";
import { Check, Loader2 } from "lucide-react";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { showToast } from "@/app/_module/utils/show-toast";
import { useUserContext } from "@/app/_module/store/contexts/userContext";

/**
 * "Join Waitlist" button for Coming Soon features. Records interest by setting a
 * flag on the user's appPreferences (no new table) — query later with e.g.
 *   SELECT email FROM users WHERE app_preferences->>'leadSniperWaitlist' = 'true';
 */
export function JoinWaitlistButton({
  featureKey,
  label = "Join Waitlist",
}: {
  featureKey: string;
  label?: string;
}) {
  const { user } = useUserContext();
  const existingStatus = (user?.user as any)?.appPreferences?.[featureKey]?.status;
  const alreadyJoined = existingStatus === "pending" || existingStatus === "approved";
  const [joined, setJoined] = useState(alreadyJoined);
  const [loading, setLoading] = useState(false);

  const join = async () => {
    if (joined || loading) return;
    setLoading(true);
    try {
      await api.post({
        url: getApiUrl("/waitlist/join"),
        body: { feature: featureKey },
        auth: true,
      });
      setJoined(true);
      showToast(
        "You're on the waitlist — we'll notify you right here in your notifications when it's live.",
        "success",
      );
    } catch {
      showToast("Couldn't join the waitlist. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  if (joined) {
    return (
      <span className="inline-flex items-center gap-2 rounded-lg bg-emerald-50 px-4 py-2.5 text-sm font-semibold text-emerald-700 ring-1 ring-emerald-600/15">
        <Check className="h-4 w-4" strokeWidth={2.75} />
        You&apos;re on the waitlist
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={join}
      disabled={loading}
      className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-sm transition-opacity hover:opacity-90 disabled:opacity-60"
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
      {label}
    </button>
  );
}
